'use client'
import { useSearchType } from '@/api/hooks'
import { PageContext } from '@/app/(site)/header-init'
import { MOBILE_STICKY_PUSHDOWN_NAVBAR_MOUNT_ID } from '@/app/components/freestar/types'
import { processSearchTerm } from '@/app/util/search-term'
import { TakeoverData } from '@/app/util/searchbar-takeover'
import { useIsMobile } from '@/app/util/use-media-query'
import { buildAuthHref } from '@/components/auth/urls'
import { AdProductContext } from '@/context/ad-product'
import FavoritesContext from '@/context/favorites-manager'
import { SearchbarContext } from '@/context/searchbar-manager'
import { useMobileMenuCopy } from '@/context/site-chrome-copy'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { useClientSideAPIKey } from '@/util/url'
import { giphyBlack } from '@giphy/colors'
import { SearchBar as SearchBar_, SearchContext, SearchContextManager } from '@giphy/react-components'
import { setGADataLayer } from 'analytics'
import { useParams, useRouter, usePathname } from 'next/navigation'
import { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import Header, { desktopHeaderWidth } from 'ui/src/components/header'
import { aboveHeaderZIndex, belowHeaderZIndex, desktopWidth } from 'ui/src/constants'
import { desktop } from 'ui/src/css'
import { removeImageTypes } from 'utils/src/media'
import { Interface } from '../../styles/fonts'
import OpenInAppCTA from '../../components/open-in-app'
import BodyScrollLock from './body-scroll-lock'
import AnimatedLogo from '../logo'
import { DesktopNav } from '../nav/desktop/desktop'
import MobileMenuFooter from '../nav/mobile-menu-footer'
import { desktopSearchbarPadding, mobileSearchbarHeight, searchbarHeight } from './constants'
import SearchDropdown from './search-dropdown'
import Takeover from './takeover'

const isTakeoverTransparent = false
const takeoverInputSize = isTakeoverTransparent ? 590 : 540
const searchbarShrunkWidth = isTakeoverTransparent ? 400 : 360
const SearchBarComponent = styled(SearchBar_)<{ $hasTakeover?: boolean; $isStuck: boolean }>`
    input {
        font-size: 22px;
        padding-left: 14px;
        font-family: ${Interface.style.fontFamily};
        ${(props) =>
            props.$hasTakeover &&
            css`
                width: ${props.$isStuck ? searchbarShrunkWidth : takeoverInputSize}px;
                flex: unset;
            `}
    }
    ${(props) =>
        props.$hasTakeover &&
        css`
            /* separate the now shorter input and the right magnifying glass */
            justify-content: space-between;
        `}
    .giphy-search-bar-cancel {
        width: 22px;
        height: 22px;
        margin-right: 10px;
        ${(props) =>
            props.$hasTakeover &&
            css`
                position: absolute;
                left: ${takeoverInputSize}px;
                margin: 0;
            `}
    }
`

const SearchbarContainer = styled.div`
    padding: 5px 0;
    ${desktop(css`
        padding: ${desktopSearchbarPadding}px 0 0;
    `)}
`

const StickyTopAdContainer = styled.div`
    background-color: ${giphyBlack};
    position: sticky;
    top: 0;
    width: 100%;
    z-index: ${aboveHeaderZIndex + 1};
`

const AnimationContainer = styled.div<{ $stickyTopOffset: number }>`
    background-color: ${giphyBlack};
    width: 100%;
    z-index: ${belowHeaderZIndex};
    position: sticky;
    /* this -1px lets us know if we're offscreen to the top and "stuck" */
    top: ${({ $stickyTopOffset }) => ($stickyTopOffset > 0 ? `${$stickyTopOffset - 1}px` : '-1px')};
`

const Container = styled.div<{ $isStuck?: boolean }>`
    width: 100%;
    ${desktop(css`
        transition: width 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) 0s;
        margin-left: auto;
    `)}
    ${({ $isStuck }) =>
        $isStuck &&
        desktop(css`
            width: calc(100% - ${desktopHeaderWidth}px);
            padding-bottom: 8px;
        `)}
`

type Props = {
    children?: ReactNode
    stickyTopContent?: ReactNode
    takeover?: TakeoverData
}

type BodyScrollLockContextProps = {
    ignoreTarget?: string
    setIgnoreTarget: (target: string) => void
}
export const BodyScrollLockContext = createContext({
    ignoreTarget: '',
    setIgnoreTarget: () => {},
} as BodyScrollLockContextProps)

const SearchInput = ({
    isStuck,
    hasInitialTerm,
    onUserInteract,
}: {
    isStuck: boolean
    hasInitialTerm: boolean
    onUserInteract: () => void
}) => {
    const { autoFocus } = useContext(SearchbarContext)
    const searchType = useSearchType()
    const { takeover } = useContext(AdProductContext)
    const placeholders = ['Search all the GIFs and Stickers', '@username to search channels']
    const [index, setIndex] = useState(0)
    const { activeChannel } = useContext(SearchContext)
    const router = useRouter()
    const pathname = usePathname()
    const isOnSearchPage = pathname?.startsWith('/search/')
    const allowFocusActivation = useRef(false)
    const searchbarContainerRef = useRef<HTMLDivElement | null>(null)

    const onEnter = useCallback(
        (term: string) => {
            if (term) {
                const cleanTerm = encodeURIComponent(removeImageTypes(term).replace(/ /g, '-').replace('/', ''))
                setGADataLayer({
                    event: 'search',
                    options: {
                        searchType: 'search',
                        searchTerm: cleanTerm,
                    },
                })
                if (activeChannel) {
                    const channel = `@${activeChannel.slug.trim().toLowerCase()}`
                    router.push(`/search/?q=${channel}+${cleanTerm}`)
                } else {
                    // Check if type is explicitly mentioned in search term
                    const hasExplicitType = [
                        'gif',
                        'gifs',
                        'sticker',
                        'stickers',
                        'clip',
                        'clips',
                        'emoji',
                        'emojis',
                        'meme',
                        'memes',
                        'reaction',
                        'reactions',
                    ].some((typeWord) => cleanTerm.toLowerCase().includes(typeWord))

                    // Determine the type suffix to use
                    let typeSuffix = ''
                    if (hasExplicitType || (isOnSearchPage && searchType !== 'gifs')) {
                        // Use explicit type from search term, or preserve current type if on search page
                        typeSuffix = searchType === 'gifs' ? '' : `-${searchType}`
                    }

                    router.push(`/search/${cleanTerm}${typeSuffix}`)
                }
            }
        },
        [activeChannel, router, searchType, isOnSearchPage]
    )
    useEffect(() => {
        // for the placeholder animation
        const timeout = setTimeout(() => setIndex(index === 0 ? 1 : 0), 3000)
        return () => clearTimeout(timeout)
    }, [index])

    useEffect(() => {
        const timeout = setTimeout(() => {
            allowFocusActivation.current = true
        }, 0)
        return () => clearTimeout(timeout)
    }, [])

    useLayoutEffect(() => {
        if (!autoFocus || !hasInitialTerm) return

        const input = searchbarContainerRef.current?.querySelector('input')
        if (!input || document.activeElement !== input) return

        const cursorPosition = input.value.length
        input.setSelectionRange(cursorPosition, cursorPosition)
    }, [autoFocus, hasInitialTerm])

    return (
        <SearchbarContainer
            ref={searchbarContainerRef}
            onFocusCapture={() => {
                if (allowFocusActivation.current) {
                    onUserInteract()
                }
            }}
            onKeyDownCapture={onUserInteract}
            onPointerDownCapture={onUserInteract}
        >
            <SearchBarComponent
                $isStuck={isStuck}
                $hasTakeover={!!takeover}
                placeholder={placeholders[index]}
                onEnter={onEnter}
                autoFocus={autoFocus}
            />
            {takeover && <Takeover takeover={takeover} />}
        </SearchbarContainer>
    )
}
const PageContainer = styled.div`
    ${desktop(css`
        position: relative;
        width: ${desktopWidth}px;
        margin: 0 auto;
    `)}
`

const HeaderSearchBar = ({ children, stickyTopContent }: Props) => {
    const apiKey = useClientSideAPIKey()
    const router = useRouter()
    const { user } = useContext(UserContext)
    const { hideSearchbar } = useContext(PageContext)
    const { term: encodedTerm = '' } = useParams<{ term?: string }>()
    const [cleanTerm] = processSearchTerm(encodedTerm)
    const pathname = usePathname()
    const { favorites } = useContext(FavoritesContext)
    const [ignoreTarget, setIgnoreTarget] = useState('')
    const isSticky = useRef<HTMLDivElement | null>(null)
    const stickyTopContentRef = useRef<HTMLDivElement | null>(null)
    const [isStuck, setIsStuck] = useState(false)
    const [hasUserActivatedSearchUi, setHasUserActivatedSearchUi] = useState(false)
    const [stickyTopOffset, setStickyTopOffset] = useState(0)
    const mobileMenuCopy = useMobileMenuCopy()

    useEffect(() => {
        let observer: IntersectionObserver
        if (isSticky.current) {
            observer = new IntersectionObserver(
                ([e]) => {
                    setIsStuck(e.intersectionRatio < 1)
                },
                {
                    threshold: [1],
                    // Treat the ad's sticky region as the top edge of the viewport.
                    rootMargin: `-${stickyTopOffset}px 1000px 1000px 1000px`,
                }
            )
            observer.observe(isSticky.current)
        }
        return () => observer?.disconnect()
    }, [stickyTopOffset])

    useEffect(() => {
        setHasUserActivatedSearchUi(false)
    }, [pathname])

    useLayoutEffect(() => {
        const element = stickyTopContentRef.current
        if (!element) {
            setStickyTopOffset(0)
            return
        }

        const updateStickyTopOffset = () => {
            setStickyTopOffset(element.offsetHeight)
        }

        updateStickyTopOffset()
        if (typeof ResizeObserver === 'undefined') {
            return
        }

        const observer = new ResizeObserver(updateStickyTopOffset)
        observer.observe(element)

        return () => observer.disconnect()
    }, [stickyTopContent])

    const onUserInteract = useCallback(() => {
        setHasUserActivatedSearchUi(true)
    }, [])

    const isMobile = useIsMobile()

    return (
        <PageContainer>
            <BodyScrollLockContext.Provider value={{ setIgnoreTarget, ignoreTarget }}>
                <SearchContextManager
                    initialTerm={cleanTerm}
                    apiKey={apiKey}
                    shouldFetchChannels={false}
                    theme={{ searchbarHeight, mobileSearchbarHeight }}
                >
                    <BodyScrollLock />

                    {stickyTopContent && (
                        <StickyTopAdContainer ref={stickyTopContentRef}>
                            <div id={MOBILE_STICKY_PUSHDOWN_NAVBAR_MOUNT_ID} />
                            {stickyTopContent}
                        </StickyTopAdContainer>
                    )}

                    {isMobile ? (
                        <Header
                            user={user}
                            onLogoClick={() => {
                                router.push('/')
                            }}
                            hasFavorites={favorites.length > 0}
                            mobileMenuFooter={<MobileMenuFooter />}
                            mobileMenuCopy={mobileMenuCopy}
                            showMobileStickyPushdownMount={false}
                            onMenuItemClick={(path, closeMenu) => {
                                if (path === '/login' || path === '/join') {
                                    closeMenu()
                                    router.push(buildAuthHref(path, pathname))
                                    return true
                                }
                                return false
                            }}
                        />
                    ) : (
                        <div className="relative">
                            <DesktopNav />
                            <div
                                className={cn(
                                    'absolute top-0 z-aboveHeader flex h-14 items-end',
                                    isStuck &&
                                        'fixed translate-y-[78px] transition-transform delay-300 duration-500 ease-[cubic-bezier(0.165,0.84,0.44,1)]'
                                )}
                                style={isStuck ? { top: `${stickyTopOffset - 70}px` } : undefined}
                            >
                                <AnimatedLogo />
                            </div>
                        </div>
                    )}

                    {!hideSearchbar && (
                        <AnimationContainer ref={isSticky} $stickyTopOffset={stickyTopOffset}>
                            <Container $isStuck={isStuck}>
                                <SearchInput
                                    isStuck={isStuck}
                                    hasInitialTerm={!!cleanTerm}
                                    onUserInteract={onUserInteract}
                                />
                                <SearchDropdown isStuck={isStuck} shouldOpen={hasUserActivatedSearchUi} />
                            </Container>
                        </AnimationContainer>
                    )}
                    {children}
                </SearchContextManager>
            </BodyScrollLockContext.Provider>
            <OpenInAppCTA />
        </PageContainer>
    )
}

export default HeaderSearchBar
